"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, Plus } from "lucide-react";
import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/app/_module/components/ui/button";
import {
  Dialog,
  DialogTrigger,
  ResponsiveDialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/app/_module/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/app/_module/components/ui/form";
import { Input } from "@/app/_module/components/ui/input";
// Switch import removed — Active Status toggle no longer shown in sub-agency creation
import { Textarea } from "@/app/_module/components/ui/textarea";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/_module/components/ui/select";
import { userServices } from "@/app/auth/sign-in/_modules/services/user-services";
import { showToast } from "@/app/_module/utils/show-toast";

import { useCreateSubAgency, useGetAgencyUsers } from "../../hooks/use-hook";
import type { CreateAgencyRequest } from "../../types/types";
import { isDemoMode } from "@/app/_module/utils/demoMode";

type CreateSubAgencyFormData = z.infer<typeof formSchema>;

interface CreateSubAgencyModalProps {
  onSuccess?: () => void;
  // When provided, the new sub-agency is created under THIS agency instead of
  // the user's current top-level agency. Used when drilling into a sub-agency's
  // detail page and clicking "Create Sub Agency" there (sub-sub creation).
  parentAgencyId?: string;
  parentAgencyName?: string;
}

const formSchema = z.object({
  name: z.string().min(2, "Name is required"),
  description: z.string().min(2, "Description is required"),
  contactEmail: z.string().email("Invalid email address"),
  isActive: z.boolean(),
  // Optional — parent owners can promote another member; agents can only create for themselves
  ownerId: z.string().optional(),
});

const CreateSubAgencyModal = function ({ onSuccess, parentAgencyId: explicitParentId, parentAgencyName: explicitParentName }: CreateSubAgencyModalProps) {
  const [open, setOpen] = useState(false);
  const { user, refetchUser } = useUserContext();
  const createSubAgencyMutation = useCreateSubAgency();

  // Determine the current user's role in the parent (the agency they're viewing).
  // Parent owners/admins get a "Promote to Owner" dropdown so they can optionally
  // spin up a new sub for one of their agents (the agent becomes owner + their
  // personal data migrates into the new sub). Agents don't see the dropdown —
  // they can only create subs for themselves.
  //
  // When `explicitParentId` is passed (e.g., from a sub-agency's detail page),
  // use that as the parent instead of the user's current top-level agency.
  const parentAgencyName =
    explicitParentName || (user.currentAgency as any)?.agency?.name || 'your parent agency';
  const currentRoleRaw =
    (user.currentAgency as any)?.agencyRole ||
    (user.currentAgency as any)?.role ||
    '';
  const isParentOwner =
    currentRoleRaw === 'owner' ||
    currentRoleRaw === 'admin' ||
    currentRoleRaw === 'agency_owner';

  // Parent-agency member list (only fetched for parent owners who can promote)
  const currentAgencyId = explicitParentId || user.currentAgency?.agency?.id || '';
  const { data: agencyUsersResponse } = useGetAgencyUsers(
    isParentOwner ? currentAgencyId : '',
  );

  const agencyUsers = React.useMemo(() => {
    if (!agencyUsersResponse) return [];
    if ('data' in (agencyUsersResponse as any) && Array.isArray((agencyUsersResponse as any).data)) {
      return (agencyUsersResponse as any).data;
    }
    if ('users' in (agencyUsersResponse as any) && Array.isArray((agencyUsersResponse as any).users)) {
      return (agencyUsersResponse as any).users;
    }
    if (Array.isArray(agencyUsersResponse)) return agencyUsersResponse as any[];
    return [];
  }, [agencyUsersResponse]);

  // Eligible to own a sub: the creator themselves, plus any full-fledged/super-agent
  // AGENT in the parent agency. Owners and admins are excluded because they can't be
  // "spun off" — their data is part of the agency's shared pool. If you want to promote
  // an admin, demote them to agent first so their data is treated as personal.
  const eligibleOwners = React.useMemo(() => {
    const currentUserId = user.user.id;
    const seen = new Set<string>();
    const eligible: Array<{ id: string; name: string; email: string; isSelf: boolean; role?: string }> = [
      {
        id: currentUserId,
        name: `${user.user.name || 'You'} (you)`,
        email: user.user.email,
        isSelf: true,
      },
    ];
    seen.add(currentUserId);
    for (const au of agencyUsers) {
      const uid = au.userId || au.user_id || au.user?.id;
      if (!uid || seen.has(uid)) continue;
      const accountType = au.user?.accountType || au.user?.selectedPlan || '';
      if (accountType !== 'full_fledged' && accountType !== 'super_agent' && accountType !== 'enterprise') continue;
      // Only agents can be promoted — owners/admins must be demoted first
      const role = au.role || '';
      if (role === 'owner' || role === 'admin') continue;
      eligible.push({
        id: uid,
        name: au.user?.name || au.user?.email || 'Unknown',
        email: au.user?.email || '',
        isSelf: false,
        role,
      });
      seen.add(uid);
    }
    return eligible;
  }, [agencyUsers, user.user.id, user.user.name, user.user.email]);

  // Check if there are admins/owners the user might want to promote but can't
  const ineligibleAdmins = React.useMemo(() => {
    return agencyUsers.filter((au: any) => {
      const uid = au.userId || au.user_id || au.user?.id;
      if (uid === user.user.id) return false;
      const role = au.role || '';
      return role === 'owner' || role === 'admin';
    });
  }, [agencyUsers, user.user.id]);

  const form = useForm<CreateSubAgencyFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      contactEmail: "",
      isActive: true,
      ownerId: user.user.id, // default: creator owns it
    },
    mode: "onChange",
  });

  // Watch ownerId so the info banner updates live when they switch
  const selectedOwnerId = form.watch('ownerId') || user.user.id;
  const isPromotingOther = isParentOwner && selectedOwnerId !== user.user.id;
  const promotedMember = isPromotingOther
    ? eligibleOwners.find((e) => e.id === selectedOwnerId)
    : null;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = form;

  const onSubmit: SubmitHandler<CreateSubAgencyFormData> = (data) => {
    // Get the current agency ID as the parent agency
    const parentAgencyId = explicitParentId || user.currentAgency?.agency?.id;

    if (!parentAgencyId) {
      return;
    }

    // Agents can't promote others — force ownerId to themselves
    const resolvedOwnerId = isParentOwner ? (data.ownerId || user.user.id) : user.user.id;
    const isSelfCreate = resolvedOwnerId === user.user.id;

    // DEMO SANDBOX: agency structure changes are real, cross-account and hard to
    // undo — a visitor moving agents or spinning up sub-agencies would reshape a
    // workspace the next visitor inherits. Offer the trial instead.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Creating sub-agencies is disabled in the demo sandbox. Start a free trial to build out your real downline.",
        }),
      );
      return;
    }

    const submitData: CreateAgencyRequest = {

      ...data,
      parentAgencyId,
      ownerId: resolvedOwnerId,
    };
    createSubAgencyMutation.mutate(submitData, {
      onSuccess: async (response) => {
        reset();
        setOpen(false);

        const newAgency = response as any;
        const newAgencyId = newAgency?.id;
        const leadsTransferred = newAgency?.leadsTransferred || 0;
        const appointmentsTransferred = newAgency?.appointmentsTransferred || 0;
        const remindersTransferred = newAgency?.remindersTransferred || 0;
        const totalTransferred = leadsTransferred + appointmentsTransferred + remindersTransferred;

        if (totalTransferred > 0) {
          const parts: string[] = [];
          if (leadsTransferred > 0) parts.push(`${leadsTransferred.toLocaleString()} lead${leadsTransferred === 1 ? '' : 's'}`);
          if (appointmentsTransferred > 0) parts.push(`${appointmentsTransferred.toLocaleString()} appointment${appointmentsTransferred === 1 ? '' : 's'}`);
          if (remindersTransferred > 0) parts.push(`${remindersTransferred.toLocaleString()} reminder${remindersTransferred === 1 ? '' : 's'}`);
          showToast(`Sub-agency created! ${parts.join(', ')} transferred.`, "success");
        } else {
          showToast("Sub-agency created successfully!", "success");
        }

        // AUTO-SWITCH RULE: only switch the creator into the new sub when they ARE
        // the new owner AND they were an agent spinning off. Otherwise (parent owner
        // self-create, or parent owner promoting someone else) the creator stays
        // in the parent dashboard and the new sub shows up in the Sub Agencies list.
        const creatorBecomesOwnerAndLeftParent = isSelfCreate && !isParentOwner;
        if (newAgencyId && creatorBecomesOwnerAndLeftParent) {
          try {
            await userServices.postAgency(newAgencyId, {
              id: newAgencyId,
              name: newAgency.name,
              description: newAgency.description,
              contactEmail: newAgency.contactEmail,
              isActive: newAgency.isActive,
              createdAt: newAgency.createdAt,
              updatedAt: newAgency.updatedAt,
            }, { silent: true });

            refetchUser((prev) => prev);
          } catch {
            // Switch failed — not critical, user can switch manually
          }
        } else {
          // Parent owner stays put — just refetch context so the new sub shows in
          // their Sub Agencies list.
          refetchUser((prev) => prev);
        }

        onSuccess?.();
      },
      onError: (error) => {

      },
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      reset();
    }
    setOpen(newOpen);
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Create Sub Agency
        </Button>
      </DialogTrigger>
      <ResponsiveDialogContent size="md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Create Sub Agency
          </DialogTitle>
          <DialogDescription>
            Add a new sub agency to your system. Fill in the required information below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {/* Context-aware info banner — three scenarios:
                  1. Parent owner promoting someone else → promotee's data moves
                  2. Parent owner creating for themselves → empty sub, no data moves
                  3. Agent self-spinning off → their own data moves with them */}
            {!isParentOwner ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                <div className="font-semibold text-amber-900 mb-1">
                  You're spinning off your own sub-agency
                </div>
                <ul className="text-amber-800 space-y-0.5 list-disc list-inside">
                  <li>You'll become the <strong>owner</strong> of your new sub-agency with full control</li>
                  <li>Your <strong>personal leads, notes, calls, and data</strong> will move from {parentAgencyName} to your new sub-agency</li>
                  <li>You'll leave {parentAgencyName} — your new sub-agency becomes your home</li>
                  <li>{parentAgencyName} owners will still have visibility into your new sub</li>
                </ul>
              </div>
            ) : isPromotingOther && promotedMember ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
                <div className="font-semibold text-emerald-900 mb-1">
                  Promoting {promotedMember.name} to sub-agency owner
                </div>
                <ul className="text-emerald-800 space-y-0.5 list-disc list-inside">
                  <li><strong>{promotedMember.name}</strong> becomes the owner of the new sub-agency</li>
                  <li>Their <strong>personal leads, notes, calls, and data</strong> in {parentAgencyName} will move to their new sub-agency</li>
                  <li>They'll leave {parentAgencyName} — their new sub-agency becomes their home</li>
                  <li>You (and all {parentAgencyName} owners) stay in {parentAgencyName} and keep visibility into their new sub</li>
                </ul>
              </div>
            ) : (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm">
                <div className="font-semibold text-blue-900 mb-1">
                  Creating a sub-agency under {parentAgencyName}
                </div>
                <ul className="text-blue-800 space-y-0.5 list-disc list-inside">
                  <li>The new sub-agency starts <strong>empty</strong> — your leads in {parentAgencyName} stay where they are</li>
                  <li>You'll own both {parentAgencyName} and the new sub-agency</li>
                  <li>You stay in the {parentAgencyName} dashboard — the new sub appears in your Sub Agencies list</li>
                  <li className="text-blue-700">Tip: use the "Promote to Owner" dropdown below to put one of your agents in charge of the new sub</li>
                </ul>
              </div>
            )}

            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sub Agency Name *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter sub agency name"
                      {...field}
                    />
                  </FormControl>
                  {!!errors.name && <FormMessage>{errors.name.message}</FormMessage>}
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description *</FormLabel>
                  <FormControl>
                    <Textarea
                      className="resize-none"
                      placeholder="Enter sub agency description"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  {!!errors.description && <FormMessage>{errors.description.message}</FormMessage>}
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name="contactEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="agency@example.com"
                      type="email"
                      {...field}
                    />
                  </FormControl>
                  {!!errors.contactEmail && <FormMessage>{errors.contactEmail.message}</FormMessage>}
                </FormItem>
              )}
            />

            {/* Promote to Owner — ONLY parent owners/admins see this.
                Agents can't promote others (backend enforces this too). */}
            {isParentOwner && (
              <FormField
                control={control}
                name="ownerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Promote to Owner</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || user.user.id}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select who will own this sub-agency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {eligibleOwners.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{e.name}</span>
                              {e.email && !e.isSelf && (
                                <span className="text-xs text-muted-foreground">{e.email}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-muted-foreground">
                      Only Full-Fledged / Super Agent agents can be promoted. Pick yourself to create an empty sub you manage directly.
                    </div>
                    {ineligibleAdmins.length > 0 && (
                      <div className="text-xs text-amber-600 mt-1">
                        Admins and owners can't be promoted to a sub-agency because their data is part of the shared agency pool. Demote them to agent first, then promote them here.
                      </div>
                    )}
                  </FormItem>
                )}
              />
            )}

            {/* Active Status toggle removed — sub-agencies are always created active.
                Only admins can deactivate agencies after creation. */}

            <DialogFooter>
              <DialogClose asChild>
                <Button
                  disabled={createSubAgencyMutation.isPending}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                disabled={!isValid || createSubAgencyMutation.isPending}
                type="submit"
              >
                {createSubAgencyMutation.isPending ? (
                  <React.Fragment>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creating...
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Sub Agency
                  </React.Fragment>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </ResponsiveDialogContent>
    </Dialog>
  );
};

export default CreateSubAgencyModal; 